import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { ArrowRight, Warning } from "@phosphor-icons/react";
import { toast } from "sonner";
import api, { setToken, formatApiErrorDetail } from "../lib/api";
import Logo from "../components/Logo";
import { useAuth } from "../context/AuthContext";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { refresh } = useAuth();
  const tokenFromUrl = params.get("token") || "";
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  if (!tokenFromUrl) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#0A0A0A] text-white p-4">
        <div className="card-flat p-8 max-w-md text-center" data-testid="reset-missing-token">
          <Warning size={32} weight="duotone" className="text-orange-400 mx-auto mb-3" />
          <h1 className="font-display text-2xl mb-2">Missing reset token.</h1>
          <p className="text-zinc-400 text-sm mb-6">This link is incomplete. Open the link from your email instead, or request a new one.</p>
          <Link to="/forgot-password" className="btn-primary justify-center">Request a new link</Link>
        </div>
      </div>
    );
  }

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (pw.length < 6) { setError("Password must be at least 6 characters"); return; }
    if (pw !== pw2) { setError("Passwords don't match"); return; }
    setLoading(true);
    try {
      const { data } = await api.post("/auth/reset-password", { token: tokenFromUrl, new_password: pw });
      // Auto-login with the returned access token
      if (data.access_token) setToken(data.access_token);
      await refresh();
      toast.success("Password reset — you're signed in");
      const role = data.user?.role;
      navigate(role === "client" ? "/client" : "/trainer", { replace: true });
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#0A0A0A] text-white p-4 sm:p-6" data-testid="reset-password-page">
      <div className="w-full max-w-md card-flat p-6 sm:p-10">
        <Link to="/" className="flex items-center gap-3 mb-6">
          <Logo size={28} />
          <div className="font-display text-xl leading-none">TRAINA</div>
        </Link>
        <div className="tracker mb-3 text-brand">[ Set a new password ]</div>
        <h1 className="font-display text-4xl sm:text-5xl mb-2">Reset.</h1>
        <p className="text-zinc-400 text-sm mb-6">Pick something only you know.</p>
        {error && <div className="mb-4 px-4 py-3 border border-red-500/40 bg-red-500/10 text-red-300 text-sm" data-testid="reset-error">{error}</div>}
        <form onSubmit={submit} className="space-y-4" data-testid="reset-form">
          <label className="block">
            <div className="tracker mb-2">New password</div>
            <input type="password" required minLength={6} value={pw} onChange={(e) => setPw(e.target.value)} className="w-full px-4 py-3 border" data-testid="reset-pw1" />
          </label>
          <label className="block">
            <div className="tracker mb-2">Confirm</div>
            <input type="password" required minLength={6} value={pw2} onChange={(e) => setPw2(e.target.value)} className="w-full px-4 py-3 border" data-testid="reset-pw2" />
          </label>
          <button type="submit" disabled={loading} className="btn-primary w-full justify-center" data-testid="reset-submit">
            {loading ? "Resetting…" : "Reset password"} <ArrowRight size={16} weight="bold" />
          </button>
        </form>
      </div>
    </div>
  );
}
